import { defineComponent, h } from 'vue';
import { QSelect, QIcon, QBtn, QTooltip } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import { wrapPropertyGridRow } from './pg2Wrap.jsx';
import { openSchemaDocumentInTab, resolveSchemaDocumentUrl } from './schemaDocumentLink.js';

function schemaAllowsCustomInput(schema) {
    // Explicit false wins — do not treat AllowVariable as a combobox on locked enums.
    if (schema?.formatData?.AllowCustomInput === false)
        return false;
    return !!(
        schema?.formatData?.AllowCustomInput
        || schema?.formatData?.AllowVariable
        || schema?.AllowVariable
    );
}

function normalizeEnumOptions(enums) {
    const items = [];
    if (!Array.isArray(enums))
        return items;

    for (let i = 0; i < enums.length; i++) {
        const item = enums[i];
        if (item && typeof item === 'object') {
            items.push({
                text: item.name ?? item.text ?? item.label ?? String(item.value ?? ''),
                value: 'value' in item ? item.value : item,
                disabled: !!item.disabled,
            });
        }
        else {
            items.push({ text: item, value: item, disabled: false });
        }
    }
    return items;
}

export default defineComponent({
    name: 'form2-select',
    components: { QSelect, QIcon, QBtn, QTooltip },
    data: function () {
        return {
            condition_expn: null,
            filter: '',
            valuetemp: undefined,
        };
    },
    created() {
        this.valuetemp = this.cmodel?.[this.schemakey];
    },
    watch: {
        modelValue(v) {
            if (this.filter)
                return;
            this.valuetemp = v;
        },
    },
    computed: {
        ...utils.forms.computed,
        allowCustom() {
            return schemaAllowsCustomInput(this.schema);
        },
        modelValue() {
            return this.cmodel?.[this.schemakey];
        },
        displayValue() {
            return this.valuetemp !== undefined ? this.valuetemp : this.modelValue;
        },
        enumOptions() {
            return normalizeEnumOptions(this.schema?.enum || this.schema?.titleMap);
        },
        selectOptions() {
            const items = this.enumOptions.slice();
            if (this.allowCustom) {
                const value = this.displayValue;
                if (value !== null && value !== undefined && value !== ''
                    && !items.some((i) => i.value == value))
                    items.push({ text: String(value), value, disabled: false });
                return items;
            }

            if (!this.modelValue)
                items.unshift({ text: 'Please select one', value: '', disabled: true });
            else if (this.schema?.$allows_null)
                items.unshift({ text: '', value: null, disabled: false });

            return items;
        },
        filteredSelectOptions() {
            const items = this.selectOptions;
            if (!this.filter)
                return items;
            const q = String(this.filter).toLowerCase();
            return items.filter((i) => String(i.text ?? '').toLowerCase().includes(q));
        },
    },
    methods: {
        ...methods,
        renderAppendSlot() {
            const buttons = [];
            const schema = this.schema;
            const modelValue = this.displayValue;

            if (schema.formatData?.buttons) {
                for (const btn of schema.formatData.buttons) {
                    buttons.push(
                        utils.tt(
                            btn.tooltip,
                            <QIcon name={btn.icon} size="xs" onClick={(e) => btn.action(e, this.cmodel[this.schemakey])} />,
                        ),
                    );
                }
            }

            if (schema.formatData?.EllipsisButton) {
                buttons.push(
                    <QIcon
                        name="mdi-dots-horizontal"
                        size="xs"
                        style="cursor: pointer;"
                        onClick={(e) =>
                            schema.formatData?.EllipsisButton(e, this.cmodel[this.schemakey], (value) =>
                                this.commitValue(value),
                            )
                        }
                    />,
                );
            }

            const schemaDocumentUrl = resolveSchemaDocumentUrl(this.schemakey, this.cmodel, modelValue);
            if (schemaDocumentUrl) {
                buttons.push(
                    <QBtn
                        flat
                        dense
                        round
                        size="sm"
                        icon="mdi-open-in-new"
                        class="q-ml-xs"
                        onClick={(e) => openSchemaDocumentInTab(e, schemaDocumentUrl, modelValue)}
                    >
                        <QTooltip>Open schema document</QTooltip>
                    </QBtn>,
                );
            }

            return buttons.length ? { append: () => buttons } : undefined;
        },
        getItemProps(item) {
            return {
                title: item.text,
                value: item.value,
                disabled: item.disabled,
            };
        },
        filterFn(val, update) {
            update(() => {
                this.filter = val;
            });
        },
        commitValue(value) {
            this.filter = '';
            this.valuetemp = value;
            this.sync(this.schemakey, value);
        },
        commitTypedValue() {
            if (!this.allowCustom)
                return;
            const next = this.valuetemp !== undefined ? this.valuetemp : this.modelValue;
            this.commitValue(next);
        },
        sync(prop, value) {
            if (Array.isArray(this.cmodel)) {
                if (prop < this.cmodel.length) this.cmodel[prop] = value;
                else {
                    while (this.cmodel.length < prop) this.cmodel.push(null);

                    this.cmodel.push(value);
                }
            } else {
                this.cmodel[prop] = value;
            }
        },
    },
    props: {
        name: '',
        schemakey: '',
        root: null,
        schema: null,
        cmodel: null,
        extra: null,
        readonly: false,
        child: 0,
        propertygrid: false,
        arrayItemInline: { type: Boolean, default: false },
    },
    render() {
        if (!this.Condition) return null;

        let desc;
        if (this.schema.description)
            desc = (
                <i
                    class="mdi mdi-information"
                    title={this.schema.description}
                    style={{ color: 'var(--v-checkboxGrey-base)', fontSize: 'small', marginLeft: '3px' }}
                ></i>
            );

        let err;
        if (this.schema.formatData?.error)
            err = utils.tt(this.schema.formatData.error, <QIcon name="mdi-alert" size="xs" color="negative" />);

        const name = <span style={{ marginLeft: this.child * 15 + 'px' }}>{this.name}</span>;
        const appendSlot = this.renderAppendSlot();
        const selectStyle = this.propertygrid ? { width: '100%', minWidth: 0 } : undefined;

        const labelSlot = () => (
            <span class="c-PropertyGrid2__labelInner">
                {name} {desc}
                {err}
            </span>
        );

        if (this.readonly)
            return wrapPropertyGridRow(this, labelSlot, () => (
                <QSelect
                    borderless
                    dense
                    hide-bottom-space
                    class="pa-0 ma-0"
                    popup-content-class="q-select-max-popup-height"
                    style={selectStyle}
                    readonly
                    modelValue={this.modelValue}
                    option-label="text"
                    option-value="value"
                    options-dense
                    options={this.selectOptions}
                    emit-value
                    map-options
                    v-slots={appendSlot}
                />
            ));

        if (this.allowCustom) {
            return wrapPropertyGridRow(this, labelSlot, () => (
                <QSelect
                    borderless
                    dense
                    hide-bottom-space
                    use-input
                    fill-input
                    hide-selected
                    autocomplete="off"
                    new-value-mode="add-unique"
                    emit-value
                    map-options
                    input-debounce={0}
                    class="pa-0 ma-0"
                    popup-content-class="q-select-max-popup-height"
                    style={selectStyle}
                    inputStyle={
                        this.propertygrid ? { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' } : undefined
                    }
                    modelValue={this.displayValue}
                    options={this.filteredSelectOptions}
                    option-label="text"
                    option-value="value"
                    options-dense
                    v-slots={appendSlot}
                    onFilter={this.filterFn}
                    onInputValue={(value) => {
                        this.valuetemp = value;
                    }}
                    onUpdate:modelValue={(value) => this.commitValue(value)}
                    onNewValue={(value) => this.commitValue(value)}
                    onPopupHide={() => {
                        this.filter = '';
                    }}
                    onBlur={() => this.commitTypedValue()}
                    onKeydown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Tab')
                            this.commitTypedValue();
                    }}
                />
            ));
        }

        return wrapPropertyGridRow(this, labelSlot, () => (
            <QSelect
                borderless
                dense
                hide-bottom-space
                class="pa-0 ma-0"
                popup-content-class="q-select-max-popup-height"
                style={selectStyle}
                modelValue={this.modelValue}
                options={this.selectOptions}
                option-label="text"
                option-value="value"
                options-dense
                emit-value
                map-options
                v-slots={appendSlot}
                onUpdate:modelValue={(value) => this.commitValue(value)}
            />
        ));
    },
});
